// Copyright ©️ 2026 eVolve MEP, LLC

import isEqual from 'react-fast-compare';

import { isNotNil } from './isNotNil';

/** Type-safe Object.keys */
export const strongKeys: <K extends string | number | symbol, V>(obj: Record<K, V> | Partial<Record<K, V>>) => K[] =
  Object.keys;

/** Type-safe Object.values */
export const strongValues: <K extends string | number | symbol, V>(obj: Record<K, V>) => V[] = Object.values;

export type StrongEntries<T> = {
  [K in keyof T]-?: [K, T[K]];
}[keyof T][];

/** Type-safe `Object.entries` */
export const strongEntries: <T extends object>(obj: T) => StrongEntries<T> = Object.entries;

/** Sorta-type-safe `Object.fromEntries` */
export const strongFromEntries: <K extends string | number | symbol, V>(entries: readonly [K, V][]) => Record<K, V> =
  Object.fromEntries;

/** Group a collection `T[]` into a `Record<K, T[]>` where `K` is generated via `keyGetter(T)` */
export const groupBy = <T, K extends string | number | symbol>(collection: readonly T[], keyGetter: (obj: T) => K) =>
  collection.reduce(
    (out, curr) => {
      const key = keyGetter(curr);
      if (isNotNil(out[key])) {
        out[key].push(curr);
      } else {
        out[key] = [curr];
      }
      return out;
    },
    {} as Record<K, T[]>,
  );

/**
 * Deep-compare two objects.
 *
 * Standard `isEqual` with an extra type check to make sure dispread types aren't passed in for `a` and `b`
 */
export const strongIsEqual: <T>(a: T, b: T) => boolean = isEqual;
